<template>
  <div class="vuse-content-wrapper">
    <vuse-section-definition
        :title="$t('Global.Main.nodesManagement')"
        :companyName="getEditCompanyInfoName || getCompanyName"
        icon="mdi-phone" />
      <v-container fluid class="fill-width">
        <v-sheet class="neu-glow with-radius">
          <v-data-table
            :loading="getLoader"
            :headers="filteredHeaders"
            :items="nodes"
            :single-expand="true"
            :expanded.sync="expanded"
            sort-by="name"
            show-expand
            class="elevation-1 crud-table px-6 neu-glow"
          >
            <template v-slot:top>
              <v-toolbar flat color="transparent" class="pt-2">
                <v-spacer />
                <v-btn color="primary" v-if="notShowByRol()" dark class="mb-2 mr-3" @click="importNode">
                  <v-icon color="white" class="mr-2" left>
                    mdi-file-import
                  </v-icon>
                  {{ $t('Global.Nodes.nodosImport') }}
                </v-btn>
                <v-btn color="primary" v-if="notShowByRol()" dark class="mb-2" @click="newNode">{{ $t('Global.Nodes.newNode') }}</v-btn>
                <nodes-dialog
                  v-if="dialogActive"
                  :activate="dialogActive"
                  :item="editedItem"
                  @create-item="createItem"
                  @update-item="updateItem"
                  @close-dialog="dialogActive=false" />
                <import-nodes-dialog
                  v-if="importNodesDialogActive"
                  :activate="importNodesDialogActive"
                  :multiple="false"
                  @filesUploaded="uploadCSV"
                  @close-dialog="importNodesDialogActive=false" />
              </v-toolbar>
            </template>

            <template #[`expanded-item`]="{ headers, item }">
              <td :colspan="headers.length" class="neu-glow-inset" >
                <nodes-expansion :item="item" :headers="headers" />
              </td>
            </template>

            <template #[`item.nodeTypeId`]="{ item }">
              <span>{{ getNodeDesc(item) }}</span>
            </template>

            <template #[`item.actions`] ="{ item }">
              <v-tooltip content-class='custom-tooltip' left>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    open-delay="200"
                    @click="editItem(item)"
                  >mdi-pencil</v-icon>
                </template>
                <span>{{ $t('Global.Nodes.edit') }}</span>
              </v-tooltip>
              <v-tooltip content-class='custom-tooltip' right>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    size="20"
                    v-bind="attrs"
                    v-on="on"
                    open-delay="200"
                    @click="deleteItem(item)"
                  >mdi-delete</v-icon>
                </template>
                <span>{{ $t('Global.Nodes.delete') }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-container>
      <confirm-dialog
        ref="confirmDialog"
        :title="confirmDialogTitle"
        :message="confirmDialogMessage"
      />
    <v-snackbar
        v-model="snackbar"
        absolute
        top
        right
        :timeout="snackbarTimeout"
        :color="snackbarColor"
        class="mt-6"
    >
      <span>{{ snackbarMessage }}</span>
      <v-icon class="ml-2" dark>{{ snackbarIcon }}</v-icon>
      <template v-slot:action="{ attrs }">
        <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          {{ $t("Global.ConfirmDialog.buttonTextClose") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import NodeApi from "@/mixins/Services/NodeApi";
import snackBar from "@/mixins/snackBarMixin";
import loader from "@/mixins/Loader";
import NodesDialog from './NodesDialog.vue'
import ImportNodesDialog from './ImportNodesDialog.vue'
import NodesExpansion from "./NodesExpansion.vue"
import ConfirmDialog from "@/components/UI/Dialogs/ConfirmDialog";
import {mapGetters} from 'vuex'
import ROL from "@/config/rol-values";
import Utils from "@/mixins/Utils";
import Logout from "@/mixins/Logout";


export default {
  name: "Nodes",
  components: {
    NodesDialog,
    ImportNodesDialog,
    NodesExpansion,
    ConfirmDialog
  },
  mixins: [NodeApi, snackBar, loader, Utils, Logout],
  data() {
    return {
      nodes: [],
      expanded: [],
      headers: [
        { text: this.$t('Global.Nodes.name'), value: "name", align: "left", sortable: true},
        { text: this.$t('Global.Nodes.description'), value: "description", align: "left", sortable: false},
        { text: this.$t('Global.Nodes.xsiNodeId'), value: "xsiNodeId", align: "left", sortable: true},
        { text: this.$t('Global.Nodes.extension'), value: "extensionNumber", align: "left", sortable: true},
        { text: this.$t('Global.Nodes.type'), value: "nodeTypeId", align: "left", sortable: true},
        { text: this.$t('Global.Nodes.actions'), value: "actions", align: "left", sortable: false},
        { text: '', value: 'data-table-expand' }
      ],
      editedItem: null,
      dialogActive: false,
      importNodesDialogActive: false,
      confirmDialogTitle: "",
      confirmDialogMessage: null,
      rol: ROL
    }
  },
  computed: {
    ...mapGetters(['getCompanyName', 'getEditCompanyInfoName', 'getRoleId', 'getDefsNodeTypes']),
    filteredHeaders() {
      if (this.headers.length) {
        return this.notShowByRol()
          ? this.headers : this.headers.filter(h => h.value !== "actions")
      }
      else return []
    }
  },
  methods: {
    async getNodesList() {
      try{
      this.setLoader(true)
        this.nodes = await this.getNodes();
      this.setLoader(false)
      } catch (e) {
        this.nodes = []
        console.error('[USERS][getNodesList] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    newNode() {
      this.dialogActive = true
      this.editedItem = null
    },
    editItem(item) {
      this.dialogActive = true
      this.editedItem = item
    },
    importNode() {
      this.importNodesDialogActive = true
    },
    async createItem(item) {
      this.confirmDialogTitle = this.$t("Global.Nodes.newNode")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.createNode(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Nodes.nodeCreatedOk"), "success", "4000", "mdi-check-circle")
        await this.getNodesList()
      } catch (e) {
        console.error('[NODES][createNode] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async updateItem(item) {
      this.confirmDialogTitle = this.$t("Global.Nodes.editNode")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.updateNode(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Nodes.nodeEditedOk"), "success", "4000", "mdi-check-circle")
        await this.getNodesList()
      } catch (e) {
        console.error('[NODES][updateNode] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async deleteItem(item) {
      this.confirmDialogTitle = this.$t("Global.Nodes.deleteNode")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.deleteNode(item.id)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Nodes.nodeDeletedOk"), "success", "4000", "mdi-check-circle")
        await this.getNodesList()
      } catch (e) {
        console.error('[NODES][deleteNode] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    getNodeDesc(item) {
      return this.getDefsNodeTypes?.find((e) => e.nodeTypeId === item.nodeTypeId)?.nodeTypeDesc
    },
    notShowByRol(){
      return ![this.rol.SITE_ADMIN,this.rol.SITE_USER,this.rol.DISPLAY_USER].includes(this.getRoleId)
    },
    async uploadCSV(dataList) {
      this.confirmDialogTitle = this.$t("Global.Nodes.nodosImport")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.importCSV(dataList)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Nodes.importOk"), "success", "4000", "mdi-check-circle")
        await this.getNodesList()
      } catch (e) {
        console.error('[NODES][uploadCSV] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },

  },
  mounted() {
    this.getNodesList()
  }

}
</script>

<style>
  .hide-actions {
    display: none !important;
  }
</style>

